import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { waServicesQuery } from './baseQueries';
import type {
  AdvisorGroupSettingModel,
  AppUserInfo,
} from '@/models/AppViewModel';
import type { WARootState } from './store';

export interface EConsentModuleConfig {
  advisorGroupId: number;
  showEconsentButton: boolean;
  showEconsentOnLogin: boolean;
  firstLoginOnly: boolean;
  showEconsentOnMobile: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UpdateEConsentModuleConfigRequest {
  showEconsentButton: boolean;
  showEconsentOnLogin: boolean;
  firstLoginOnly: boolean;
}

export interface EConsentPromptWindowConfig {
  advisorGroupId: number;
  title: string;
  descriptionHtml: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UpdateEConsentPromptWindowRequest {
  title: string;
  descriptionHtml: string;
}

interface EConsentModuleConfigApiResponse {
  advisorGroupId?: number;
  advisor_group_id?: number;
  showEconsentButton?: boolean;
  show_econsent_button?: boolean;
  showEconsentOnLogin?: boolean;
  show_econsent_on_login?: boolean;
  firstLoginOnly?: boolean;
  first_login_only?: boolean;
  updatedAt?: string;
  updated_at?: string;
  updatedBy?: string;
  updated_by?: string;
}

interface EConsentPromptWindowApiResponse {
  advisorGroupId?: number;
  advisor_group_id?: number;
  title?: string;
  descriptionHtml?: string;
  description_html?: string;
  updatedAt?: string;
  updated_at?: string;
  updatedBy?: string;
  updated_by?: string;
}

interface RuntimeEConsentPromptWindowApiResponse {
  AdvisorGroupId?: number;
  PromptActionLabel?: string;
  Description?: string;
}

export const DEFAULT_ECONSENT_CONFIG_EXISTING: Omit<
  EConsentModuleConfig,
  'advisorGroupId'
> = {
  showEconsentButton: true,
  showEconsentOnLogin: false,
  firstLoginOnly: false,
  showEconsentOnMobile: true,
};

export const DEFAULT_ECONSENT_CONFIG_COMMERCE: Omit<
  EConsentModuleConfig,
  'advisorGroupId'
> = {
  showEconsentButton: false,
  showEconsentOnLogin: true,
  firstLoginOnly: false,
  showEconsentOnMobile: true,
};

export const DEFAULT_ECONSENT_PROMPT_WINDOW: Omit<
  EConsentPromptWindowConfig,
  'advisorGroupId'
> = {
  title: 'Electronic Statement Consent',
  // intentionally empty: prompt copy is org-approved and comes from the consent-status
  // response, so no generic default may render while that request is in flight (EC-88)
  descriptionHtml: '',
};

export const MAX_ECONSENT_PROMPT_TITLE_LENGTH = 60;

function isCommerceTenant(userInfo: AppUserInfo | undefined): boolean {
  const appName = userInfo?.AppName?.toLowerCase() ?? '';
  return appName.includes('commerce');
}

export function resolveDefaultEConsentConfig(
  userInfo: AppUserInfo | undefined,
): Omit<EConsentModuleConfig, 'advisorGroupId'> {
  return isCommerceTenant(userInfo)
    ? DEFAULT_ECONSENT_CONFIG_COMMERCE
    : DEFAULT_ECONSENT_CONFIG_EXISTING;
}

function parseBooleanSetting(
  settings: AdvisorGroupSettingModel[] | undefined,
  key: string,
): boolean | undefined {
  const normalizedValue = settings
    ?.find((setting) => setting.Key === key)
    ?.Value?.trim()
    .toLowerCase();

  if (!normalizedValue) {
    return undefined;
  }

  if (['true', '1', 'yes'].includes(normalizedValue)) {
    return true;
  }

  if (['false', '0', 'no'].includes(normalizedValue)) {
    return false;
  }

  return undefined;
}

export function resolveRuntimeEConsentConfig(
  advisorGroupId: number,
  userInfo: AppUserInfo | undefined,
): EConsentModuleConfig {
  const fallbackConfig = resolveDefaultEConsentConfig(userInfo);
  const settings = userInfo?.AGSettings;

  return {
    advisorGroupId,
    showEconsentButton:
      parseBooleanSetting(settings, 'show_econsent_button') ??
      fallbackConfig.showEconsentButton,
    showEconsentOnLogin:
      parseBooleanSetting(settings, 'show_econsent_on_login') ??
      fallbackConfig.showEconsentOnLogin,
    firstLoginOnly:
      parseBooleanSetting(settings, 'first_login_only') ??
      fallbackConfig.firstLoginOnly,
    showEconsentOnMobile:
      parseBooleanSetting(settings, 'show_econsent_on_mobile') ??
      fallbackConfig.showEconsentOnMobile,
  };
}

export function resolveDefaultEConsentPromptWindowConfig(): Omit<
  EConsentPromptWindowConfig,
  'advisorGroupId'
> {
  return DEFAULT_ECONSENT_PROMPT_WINDOW;
}

export function resolveEConsentPromptTitle(
  title: string | null | undefined,
  fallbackTitle = DEFAULT_ECONSENT_PROMPT_WINDOW.title,
): string {
  const trimmedTitle = title?.trim() || fallbackTitle;
  return trimmedTitle.slice(0, MAX_ECONSENT_PROMPT_TITLE_LENGTH);
}

const mapConfigResponse = (
  advisorGroupId: number,
  response: EConsentModuleConfigApiResponse | undefined,
  fallbackConfig: Omit<EConsentModuleConfig, 'advisorGroupId'>,
): EConsentModuleConfig => ({
  advisorGroupId:
    response?.advisorGroupId ?? response?.advisor_group_id ?? advisorGroupId,
  showEconsentButton:
    response?.showEconsentButton ??
    response?.show_econsent_button ??
    fallbackConfig.showEconsentButton,
  showEconsentOnLogin:
    response?.showEconsentOnLogin ??
    response?.show_econsent_on_login ??
    fallbackConfig.showEconsentOnLogin,
  firstLoginOnly:
    response?.firstLoginOnly ??
    response?.first_login_only ??
    fallbackConfig.firstLoginOnly,
  // backend doesn't return this field yet; always falls back until a real config endpoint exists
  showEconsentOnMobile: fallbackConfig.showEconsentOnMobile,
  updatedAt: response?.updatedAt ?? response?.updated_at,
  updatedBy: response?.updatedBy ?? response?.updated_by,
});

function getFetchBaseQueryStatus(
  error: FetchBaseQueryError,
): number | undefined {
  const status = error.status;
  if (typeof status === 'number') {
    return status;
  }

  if (status === 'PARSING_ERROR') {
    return error.originalStatus;
  }

  return undefined;
}

const mapPromptWindowResponse = (
  advisorGroupId: number,
  response: EConsentPromptWindowApiResponse | undefined,
  fallbackConfig: Omit<EConsentPromptWindowConfig, 'advisorGroupId'>,
): EConsentPromptWindowConfig => ({
  advisorGroupId:
    response?.advisorGroupId ?? response?.advisor_group_id ?? advisorGroupId,
  title: response?.title ?? fallbackConfig.title,
  descriptionHtml:
    response?.descriptionHtml ??
    response?.description_html ??
    fallbackConfig.descriptionHtml,
  updatedAt: response?.updatedAt ?? response?.updated_at,
  updatedBy: response?.updatedBy ?? response?.updated_by,
});

const mapRuntimePromptWindowResponse = (
  advisorGroupId: number,
  response: RuntimeEConsentPromptWindowApiResponse | undefined,
  fallbackConfig: Omit<EConsentPromptWindowConfig, 'advisorGroupId'>,
): EConsentPromptWindowConfig => ({
  advisorGroupId: response?.AdvisorGroupId ?? advisorGroupId,
  title: response?.PromptActionLabel ?? fallbackConfig.title,
  descriptionHtml: response?.Description ?? fallbackConfig.descriptionHtml,
});

export const eConsentConfigApi = createApi({
  reducerPath: 'eConsentConfigApi',
  baseQuery: waServicesQuery,
  tagTypes: ['EConsentConfig', 'EConsentPromptWindow'],
  endpoints: (builder) => ({
    getEConsentConfig: builder.query<EConsentModuleConfig, number>({
      async queryFn(advisorGroupId, api, _extraOptions, baseQuery) {
        const state = api.getState() as WARootState;
        const fallbackConfig = resolveDefaultEConsentConfig(
          state.appView.UserInfo,
        );

        const result = await baseQuery({
          url: `/api/firm/advisorGroups/${String(advisorGroupId)}/econsent/config`,
          method: 'GET',
        });

        if (result.error) {
          const status = getFetchBaseQueryStatus(result.error);

          if (status === 404) {
            return {
              data: {
                advisorGroupId,
                ...fallbackConfig,
              },
            };
          }

          return { error: result.error };
        }

        return {
          data: mapConfigResponse(
            advisorGroupId,
            result.data as EConsentModuleConfigApiResponse,
            fallbackConfig,
          ),
        };
      },
      providesTags: (_result, _error, advisorGroupId) => [
        { type: 'EConsentConfig', id: String(advisorGroupId) },
      ],
    }),
    updateEConsentPromptWindowConfig: builder.mutation<
      EConsentPromptWindowConfig,
      { advisorGroupId: number; config: UpdateEConsentPromptWindowRequest }
    >({
      query: ({ advisorGroupId, config }) => ({
        url: `/api/firm/advisorGroups/${String(advisorGroupId)}/econsent/prompt-window`,
        method: 'PUT',
        body: {
          title: config.title,
          description_html: config.descriptionHtml,
        },
      }),
      transformResponse: (
        response: EConsentPromptWindowApiResponse,
        _meta,
        arg,
      ) =>
        mapPromptWindowResponse(arg.advisorGroupId, response, {
          title: arg.config.title,
          descriptionHtml: arg.config.descriptionHtml,
        }),
      invalidatesTags: (_result, _error, { advisorGroupId }) => [
        { type: 'EConsentPromptWindow', id: String(advisorGroupId) },
      ],
    }),
    updateEConsentConfig: builder.mutation<
      EConsentModuleConfig,
      { advisorGroupId: number; config: UpdateEConsentModuleConfigRequest }
    >({
      query: ({ advisorGroupId, config }) => ({
        url: `/api/firm/advisorGroups/${String(advisorGroupId)}/econsent/config`,
        method: 'PUT',
        body: {
          show_econsent_button: config.showEconsentButton,
          show_econsent_on_login: config.showEconsentOnLogin,
          first_login_only: config.firstLoginOnly,
        },
      }),
      transformResponse: (
        response: EConsentModuleConfigApiResponse,
        _meta,
        arg,
      ) =>
        mapConfigResponse(arg.advisorGroupId, response, {
          showEconsentButton: arg.config.showEconsentButton,
          showEconsentOnLogin: arg.config.showEconsentOnLogin,
          firstLoginOnly: arg.config.firstLoginOnly,
          // this mutation doesn't support writing this field yet (admin UI is out of scope);
          // static placeholder only, not tenant-aware
          showEconsentOnMobile:
            DEFAULT_ECONSENT_CONFIG_EXISTING.showEconsentOnMobile,
        }),
      invalidatesTags: (_result, _error, { advisorGroupId }) => [
        { type: 'EConsentConfig', id: String(advisorGroupId) },
      ],
    }),
    getEConsentPromptWindowConfig: builder.query<
      EConsentPromptWindowConfig,
      number
    >({
      async queryFn(advisorGroupId, _api, _extraOptions, baseQuery) {
        const fallbackConfig = resolveDefaultEConsentPromptWindowConfig();

        const result = await baseQuery({
          url: `/api/firm/advisorGroups/${String(advisorGroupId)}/econsent/prompt-window`,
          method: 'GET',
        });

        if (result.error) {
          const status = getFetchBaseQueryStatus(result.error);

          if (status === 404) {
            return {
              data: {
                advisorGroupId,
                ...fallbackConfig,
              },
            };
          }

          return { error: result.error };
        }

        return {
          data: mapPromptWindowResponse(
            advisorGroupId,
            result.data as EConsentPromptWindowApiResponse,
            fallbackConfig,
          ),
        };
      },
      providesTags: (_result, _error, advisorGroupId) => [
        { type: 'EConsentPromptWindow', id: String(advisorGroupId) },
      ],
    }),
    getRuntimeEConsentPromptWindowConfig: builder.query<
      EConsentPromptWindowConfig,
      number
    >({
      async queryFn(advisorGroupId, _api, _extraOptions, baseQuery) {
        const fallbackConfig = resolveDefaultEConsentPromptWindowConfig();

        const result = await baseQuery({
          url: `/api/investor/EConsent`,
          method: 'GET',
        });

        if (result.error) {
          const status = getFetchBaseQueryStatus(result.error);

          if (status === 404) {
            return {
              data: {
                advisorGroupId,
                ...fallbackConfig,
              },
            };
          }

          return { error: result.error };
        }

        return {
          data: mapRuntimePromptWindowResponse(
            advisorGroupId,
            result.data as RuntimeEConsentPromptWindowApiResponse,
            fallbackConfig,
          ),
        };
      },
    }),
  }),
});

export const {
  useGetEConsentConfigQuery,
  useUpdateEConsentConfigMutation,
  useGetEConsentPromptWindowConfigQuery,
  useGetRuntimeEConsentPromptWindowConfigQuery,
  useUpdateEConsentPromptWindowConfigMutation,
} = eConsentConfigApi;
